import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AutoAwesome } from '@mui/icons-material';

import { BuildCardBuildPreview } from '../build/BuildCards';
import spellService from '../../services/SpellService';
import { spellTypeIdForBuild } from '../../utils/spellCardStatIds';
import { suggestAiBuildName } from '../../utils/suggestAiBuildName';
import {
    t,
    buildRelevantStatRows,
    distributeStatsByColumnCapacity,
} from './aiChatUtils';
import './AiChatBuildCard.css';

const STAT_ROW_HEIGHT_FALLBACK_PX = 22;

function StatRow({ row }) {
    return (
        <div className="ai-chat-build-card-stat-row">
            {row.icon
                ? <img src={row.icon} alt="" />
                : <AutoAwesome className="ai-chat-build-card-stat-fallback-icon" fontSize="inherit" />}
            <strong className="ai-chat-build-card-stat-value">{row.value}</strong>
            <span className="ai-chat-build-card-stat-label">{row.label}</span>
        </div>
    );
}

function useStatColumnLayout(statCount) {
    const statPanelRef = useRef(null);
    const [rowsPerColumn, setRowsPerColumn] = useState(99);
    const [stackColumns, setStackColumns] = useState(false);

    useLayoutEffect(() => {
        const mq = window.matchMedia('(max-width: 48rem)');
        const syncStack = () => setStackColumns(mq.matches);
        syncStack();
        mq.addEventListener('change', syncStack);
        return () => mq.removeEventListener('change', syncStack);
    }, []);

    useLayoutEffect(() => {
        const panel = statPanelRef.current;
        if (!panel) return undefined;

        const update = () => {
            if (stackColumns) {
                setRowsPerColumn(Math.max(1, statCount));
                return;
            }

            const panelH = panel.clientHeight;
            const rowEl = panel.querySelector('.ai-chat-build-card-stat-row');
            const rowH = rowEl?.offsetHeight || STAT_ROW_HEIGHT_FALLBACK_PX;
            if (panelH <= 0 || rowH <= 0) return;

            setRowsPerColumn(Math.max(1, Math.floor(panelH / rowH)));
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(panel);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [statCount, stackColumns]);

    return { statPanelRef, rowsPerColumn };
}

export default function AiChatBuildCard({ message, lang, onOpen, openBusy, disabled }) {
    const { build, draft, genConfig } = message;
    const [spellData, setSpellData] = useState(null);

    const typeId = useMemo(() => spellTypeIdForBuild(build, draft), [build, draft]);

    useEffect(() => {
        let cancelled = false;
        if (!typeId) {
            setSpellData(null);
            return undefined;
        }
        const cached = spellService.getCachedClassSpells(typeId);
        if (cached) {
            setSpellData(cached);
            return undefined;
        }
        spellService.getClassSpells(typeId)
            .then((data) => {
                if (!cancelled) setSpellData(data);
            })
            .catch(() => {
                if (!cancelled) setSpellData(null);
            });
        return () => {
            cancelled = true;
        };
    }, [typeId]);

    const statRows = useMemo(
        () => buildRelevantStatRows(build, draft, lang, {
            spellData,
            defenseShare: genConfig?.defenseShare,
        }),
        [build, draft, lang, spellData, genConfig?.defenseShare]
    );

    const { statPanelRef, rowsPerColumn } = useStatColumnLayout(statRows.length);

    const columns = useMemo(
        () => distributeStatsByColumnCapacity(statRows, rowsPerColumn),
        [statRows, rowsPerColumn]
    );

    const buildName = useMemo(
        () => suggestAiBuildName({ draft, lang, userRequest: message?.userRequest || '' }),
        [draft, lang, message?.userRequest]
    );

    const buildLevel = Number(build?.lvl) || 200;

    return (
        <article className="ai-chat-build-card mybuilds-card">
            <div className="ai-chat-build-card-preview">
                <BuildCardBuildPreview buildModel={build} lang={lang} />
            </div>
            <div className="ai-chat-build-card-details">
                <div className="ai-chat-build-card-level-row">
                    <div className="ai-chat-build-card-title">
                        <span className="ai-chat-build-card-name">{buildName}</span>
                        <span className="ai-chat-build-card-level">
                            {t(lang, 'level')} {buildLevel}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => onOpen(message)}
                        disabled={disabled}
                    >
                        {openBusy ? t(lang, 'opening') : t(lang, 'openBuild')}
                    </button>
                </div>
                {statRows.length > 0 && (
                    <div className="ai-chat-build-card-stat-panel" ref={statPanelRef}>
                        <div className="ai-chat-build-card-stat-columns">
                            {columns.map((column, colIdx) => (
                                <div key={colIdx} className="ai-chat-build-card-stat-col">
                                    {column.map((row) => (
                                        <StatRow key={row.key} row={row} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}
